// Global packages and components
import { clsx } from 'clsx'
import { Form, useSubmit, useRouteLoaderData } from '@remix-run/react'
import { useState, useRef } from 'react'
import { SearchDropdown } from '~/components'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useSearch } from '~/hooks'

// Types
import type { RootDataProps } from '~/types'

interface Props {
  featured?: boolean
  buttonText?: string
  placeholder?: string
}

// Main export
const ProductSearch = ({
  featured,
  buttonText,
  placeholder = 'Search...',
}: Props) => {
  const formRef = useRef<HTMLFormElement>(null)
  const submit = useSubmit()
  const { popularSearches } = useRouteLoaderData('root') as RootDataProps
  const [query, setQuery] = useState<string>('')
  const [hasFocus, setHasFocus] = useState<boolean>(false)
  const { loading, products, suggestions } = useSearch({
    query,
  })

  const handleSubmit = (newQuery?: string) => {
    // Update string in input if passed
    if (newQuery) {
      setQuery(newQuery)
    }

    // Create form data object
    const formData = new FormData()
    formData.append('query', newQuery ?? query)

    // Remove active focus from dropdown
    setHasFocus(false)

    // Submit to results route
    submit(formData, { action: '/p', method: 'get' })
  }

  return (
    <div className="relative z-30 w-full">
      <Form
        data-cs-encrypt
        ref={formRef}
        className="relative z-[3] flex w-full"
        action="/p"
        method="get"
        onSubmit={e => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <div className="relative w-full">
          <input
            data-cs-encrypt
            name="query"
            type="text"
            value={query}
            autoComplete="off"
            onChange={e => {
              setHasFocus(true)
              setQuery(e.target.value)
            }}
            className={clsx(
              'w-full max-w-full rounded-l-lg border border-neutral-300 py-3 pl-3 pr-8 text-sm outline-none',
              {
                'md:rounded-full md:p-6 md:text-xl': featured,
              }
            )}
            placeholder={placeholder}
            onFocus={() => {
              setHasFocus(true)
            }}
            onBlur={() => {
              setHasFocus(false)
            }}
            onKeyDown={e => {
              if (e.key == 'Enter') {
                e.currentTarget.blur()
                formRef?.current?.submit()
              }
            }}
          />
          {query && (
            <button
              aria-label="clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2"
              onClick={() => setQuery('')}
            >
              <XMarkIcon className="size-5 text-neutral-400" />
            </button>
          )}
        </div>
        <button
          aria-label={buttonText ?? 'Search'}
          className={clsx(
            'flex cursor-pointer items-center rounded-r-lg bg-teal-green p-3 text-white',
            {
              'md:absolute md:right-3 md:top-1/2 md:-translate-y-1/2 md:rounded-full md:px-8':
                featured,
            }
          )}
        >
          {buttonText && (
            <span className="hidden font-semibold md:inline-block">
              {buttonText}
            </span>
          )}
          <MagnifyingGlassIcon
            className={clsx('size-5', {
              'md:hidden': buttonText,
            })}
          />
        </button>
      </Form>
      <SearchDropdown
        loading={loading}
        featured={featured}
        hasFocus={hasFocus}
        setHasFocus={setHasFocus}
        popular={popularSearches}
        suggestions={suggestions}
        products={products}
        onClick={handleSubmit}
      />
    </div>
  )
}

export default ProductSearch
